/**
 * Lightweight RRULE expansion for common recurrence patterns.
 * Supports: FREQ=DAILY, WEEKLY (BYDAY), MONTHLY (BYDAY with ordinal or BYMONTHDAY).
 *
 * expandRecurrence(rrule, startDate, endDate) → array of Date objects
 */

const DAY_MAP = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };

function parseRRule(rrule) {
  const parts = {};
  for (const segment of rrule.split(";")) {
    const [key, val] = segment.split("=");
    parts[key] = val;
  }
  return parts;
}

function nthWeekdayOfMonth(year, month, weekday, n) {
  // Find the nth occurrence of weekday in month
  const first = new Date(year, month, 1);
  let day = first.getDay();
  let offset = (weekday - day + 7) % 7;
  let date = 1 + offset + (n - 1) * 7;
  if (date > new Date(year, month + 1, 0).getDate()) return null;
  return new Date(year, month, date);
}

export function expandRecurrence(rrule, startDate, endDate) {
  if (!rrule) return [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  const parts = parseRRule(rrule);
  const freq = parts.FREQ;
  const interval = parseInt(parts.INTERVAL) || 1;
  const dates = [];
  const maxDates = 200;

  if (freq === "DAILY") {
    let d = new Date(start);
    while (d <= end && dates.length < maxDates) {
      dates.push(new Date(d));
      d.setDate(d.getDate() + interval);
    }
    return dates;
  }

  if (freq === "WEEKLY") {
    const byDay = parts.BYDAY ? parts.BYDAY.split(",").map((d) => DAY_MAP[d]).filter((d) => d !== undefined) : [start.getDay()];
    let d = new Date(start);
    d.setDate(d.getDate() - d.getDay()); // back to Sunday of start week
    while (d <= end && dates.length < maxDates) {
      for (const wd of byDay) {
        const candidate = new Date(d);
        candidate.setDate(candidate.getDate() + wd);
        if (candidate >= start && candidate <= end) {
          dates.push(new Date(candidate));
        }
      }
      d.setDate(d.getDate() + 7 * interval);
    }
    return dates;
  }

  if (freq === "MONTHLY") {
    // BYMONTHDAY=15 — fixed day of month
    if (parts.BYMONTHDAY) {
      const day = parseInt(parts.BYMONTHDAY);
      let m = new Date(start.getFullYear(), start.getMonth(), day);
      if (m < start) m.setMonth(m.getMonth() + 1);
      while (m <= end && dates.length < maxDates) {
        const lastDay = new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
        if (day <= lastDay) dates.push(new Date(m));
        m.setMonth(m.getMonth() + interval);
      }
      return dates;
    }

    // BYDAY=4WE — nth weekday of month
    if (parts.BYDAY) {
      const match = parts.BYDAY.match(/^(-?\d)?(\w{2})$/);
      if (match) {
        const n = parseInt(match[1]) || 1;
        const wd = DAY_MAP[match[2]];
        if (wd === undefined) return dates;

        let m = new Date(start.getFullYear(), start.getMonth(), 1);
        while (m <= end && dates.length < maxDates) {
          const d = nthWeekdayOfMonth(m.getFullYear(), m.getMonth(), wd, n);
          if (d && d >= start && d <= end) dates.push(d);
          m.setMonth(m.getMonth() + interval);
        }
        return dates;
      }
    }
  }

  return dates;
}

/**
 * Generate a human-readable description of an RRULE.
 */
const DAY_NAMES = { SU: "Sunday", MO: "Monday", TU: "Tuesday", WE: "Wednesday", TH: "Thursday", FR: "Friday", SA: "Saturday" };
const ORDINALS = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th", 5: "5th" };

export function describeRecurrence(rrule) {
  if (!rrule) return "";
  const parts = parseRRule(rrule);
  const freq = parts.FREQ;
  const interval = parseInt(parts.INTERVAL) || 1;

  if (freq === "DAILY") return interval === 1 ? "Every day" : `Every ${interval} days`;

  if (freq === "WEEKLY") {
    const days = parts.BYDAY ? parts.BYDAY.split(",").map((d) => DAY_NAMES[d] || d).join(", ") : "";
    return interval === 1 ? `Weekly on ${days}` : `Every ${interval} weeks on ${days}`;
  }

  if (freq === "MONTHLY") {
    if (parts.BYMONTHDAY) {
      const day = parts.BYMONTHDAY;
      return interval === 1 ? `Monthly on the ${day}th` : `Every ${interval} months on the ${day}th`;
    }
    if (parts.BYDAY) {
      const match = parts.BYDAY.match(/^(-?\d)?(\w{2})$/);
      if (match) {
        const n = parseInt(match[1]) || 1;
        const dayName = DAY_NAMES[match[2]] || match[2];
        return interval === 1 ? `Monthly on the ${ORDINALS[n] || n + "th"} ${dayName}` : `Every ${interval} months on the ${ORDINALS[n] || n + "th"} ${dayName}`;
      }
    }
  }

  return rrule;
}

/**
 * Common recurrence presets for the UI picker.
 */
export const RECURRENCE_PRESETS = [
  { label: "None", value: null },
  { label: "Daily", value: "FREQ=DAILY" },
  { label: "Weekly on Monday", value: "FREQ=WEEKLY;BYDAY=MO" },
  { label: "Weekly on Wednesday", value: "FREQ=WEEKLY;BYDAY=WE" },
  { label: "Weekly on Friday", value: "FREQ=WEEKLY;BYDAY=FR" },
  { label: "1st Monday of month", value: "FREQ=MONTHLY;BYDAY=1MO" },
  { label: "2nd Wednesday of month", value: "FREQ=MONTHLY;BYDAY=2WE" },
  { label: "4th Wednesday of month", value: "FREQ=MONTHLY;BYDAY=4WE" },
  { label: "15th of month", value: "FREQ=MONTHLY;BYMONTHDAY=15" },
  { label: "Last day of month", value: "FREQ=MONTHLY;BYMONTHDAY=28" },
];
